import React, { useState } from 'react';
import { BookOpen, Check, X, AlertCircle, Award } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { FISCAL_QUIZZES } from '../../data/fiscalQuizzes';
import { LEGAL_FORMS, FISCAL_REGIMES } from '../../data/legalForms';

/**
 * FiscalQuizModal — one-shot pedagogical modal that pops when the player
 * first crosses a fiscal threshold. Mounted at the App root so it overlays
 * any page. Doesn't dismiss until the player answers.
 */
export default function FiscalQuizModal() {
  const pending = useGameStore(s => s.pendingFiscalQuiz);
  const answer = useGameStore(s => s.answerFiscalQuiz);
  const [selected, setSelected] = useState(null);
  const [revealed, setRevealed] = useState(false);

  if (!pending) return null;
  const quiz = FISCAL_QUIZZES[pending.quizId];
  if (!quiz) return null;

  const selectedOpt = quiz.options.find(o => o.id === selected);
  const targetForm = quiz.targetForm ? LEGAL_FORMS[quiz.targetForm] : null;
  const targetRegime = quiz.targetRegime ? FISCAL_REGIMES[quiz.targetRegime] : null;

  const handleSubmit = () => {
    if (!selected) return;
    setRevealed(true);
  };

  const handleClose = () => {
    // Send answer + reset.
    if (selected) answer(selected);
    setSelected(null);
    setRevealed(false);
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4 backdrop-blur-sm"
      data-testid="fiscal-quiz-modal-overlay"
    >
      <div
        className="bg-ink-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[88vh] overflow-y-auto border-2 border-amber-500/40"
        data-testid="fiscal-quiz-modal"
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-ink-600 bg-gradient-to-r from-amber-700/30 to-purple-900/30">
          <div className="flex items-center gap-2 mb-1">
            <BookOpen className="w-4 h-4 text-amber-300" />
            <span className="text-[10px] uppercase tracking-wider font-mono text-amber-300 font-bold">
              {quiz.sender} · {quiz.title}
            </span>
          </div>
          <h2 className="text-xl font-bold text-slate-100 leading-tight">
            {quiz.question}
          </h2>
        </div>

        {/* Intro */}
        <div className="px-6 py-3 text-xs text-slate-400 leading-relaxed border-b border-ink-600 bg-amber-500/5">
          {quiz.intro}
        </div>

        {/* Options */}
        <div className="px-6 py-4 space-y-2">
          {quiz.options.map(opt => {
            const isSelected = selected === opt.id;
            const isCorrect = revealed && opt.correct;
            const isWrong = revealed && isSelected && !opt.correct;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => !revealed && setSelected(opt.id)}
                disabled={revealed}
                className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all flex items-start gap-2 ${
                  isCorrect
                    ? 'border-emerald-400 bg-emerald-500/15 text-emerald-100'
                    : isWrong
                      ? 'border-rose-400 bg-rose-500/15 text-rose-100'
                      : isSelected
                        ? 'border-amber-400 bg-amber-500/10 text-amber-100'
                        : 'border-ink-600 bg-ink-700/40 text-slate-300 hover:border-slate-500'
                }`}
                data-testid={`quiz-opt-${opt.id}`}
              >
                <span className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                  isCorrect ? 'bg-emerald-500' : isWrong ? 'bg-rose-500' : isSelected ? 'bg-amber-500' : 'bg-ink-600'
                }`}>
                  {isCorrect ? <Check className="w-3 h-3 text-white" />
                    : isWrong ? <X className="w-3 h-3 text-white" />
                    : <span className="text-[10px] font-bold text-slate-300">{quiz.options.indexOf(opt) + 1}</span>}
                </span>
                <div className="flex-1">
                  <p className="text-sm font-semibold leading-tight">{opt.label}</p>
                  {revealed && (isCorrect || isWrong) && (
                    <p className="text-[11px] mt-1.5 italic leading-relaxed opacity-90" data-testid={`quiz-explanation-${opt.id}`}>
                      {opt.explanation}
                    </p>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Outcome banner (after reveal) */}
        {revealed && selectedOpt && (
          <div className={`px-6 py-3 border-t border-ink-600 ${selectedOpt.correct ? 'bg-emerald-500/10' : 'bg-rose-500/10'}`} data-testid="quiz-outcome">
            <div className="flex items-center gap-2">
              {selectedOpt.correct ? (
                <>
                  <Award className="w-5 h-5 text-emerald-300" />
                  <p className="text-sm text-emerald-200 font-semibold">
                    Bravo ! +{quiz.correctReputationBonus} points de réputation.
                  </p>
                </>
              ) : (
                <>
                  <AlertCircle className="w-5 h-5 text-rose-300" />
                  <p className="text-sm text-rose-200 font-semibold">
                    Pas de bonus pédagogique. La bascule reste programmée.
                  </p>
                </>
              )}
            </div>
            <p className="text-[11px] text-slate-400 mt-1">
              Le passage en {' '}
              {targetForm && <strong className="text-slate-200">{targetForm.shortName}</strong>}
              {targetForm && targetRegime && ' au régime '}
              {targetRegime && <strong className="text-slate-200">{targetRegime.name}</strong>}
              {' '}aura lieu le 01/01/{pending.plannedYear}. Vous pouvez aussi anticiper depuis Fiscalité → Statut Juridique.
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-ink-600 flex items-center justify-end gap-2">
          {!revealed ? (
            <button
              onClick={handleSubmit}
              disabled={!selected}
              className="px-5 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-bold text-sm disabled:opacity-40 transition-colors"
              data-testid="quiz-submit"
            >
              Valider ma réponse
            </button>
          ) : (
            <button
              onClick={handleClose}
              className="px-5 py-2 rounded-lg bg-slate-600 hover:bg-slate-700 text-white font-bold text-sm transition-colors"
              data-testid="quiz-close"
            >
              Fermer
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
